import React, { useState } from 'react';
import { Outlet, useNavigate, useLocation } from 'react-router-dom';
import {
  Box,
  Drawer,
  AppBar,
  Toolbar,
  List,
  Typography,
  Divider,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  IconButton,
  Avatar,
  Tooltip,
  useTheme,
  useMediaQuery,
  BottomNavigation,
  BottomNavigationAction,
  Paper,
} from '@mui/material';
import MenuIcon from '@mui/icons-material/Menu';
import DashboardIcon from '@mui/icons-material/Dashboard';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import SchoolIcon from '@mui/icons-material/School';
import BarChartIcon from '@mui/icons-material/BarChart';
import SettingsIcon from '@mui/icons-material/Settings';
import LogoutIcon from '@mui/icons-material/Logout';
import Brightness4Icon from '@mui/icons-material/Brightness4';
import Brightness7Icon from '@mui/icons-material/Brightness7';
import LanguageIcon from '@mui/icons-material/Language';
import { useAuth } from '../context/AuthContext';
import { useTranslation } from 'react-i18next';

const drawerWidth = 240;

interface LayoutProps {
  darkMode: boolean;
  toggleDarkMode: () => void;
}

export const Layout: React.FC<LayoutProps> = ({ darkMode, toggleDarkMode }) => {
  const [mobileOpen, setMobileOpen] = useState(false);
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const { i18n, t } = useTranslation();

  const toggleLanguage = () => {
    const newLang = i18n.language === 'ru' ? 'en' : 'ru';
    i18n.changeLanguage(newLang);
  };

  const menuItems = [
    { text: t('navigation.home'), icon: <DashboardIcon />, path: '/', roles: [] },
    { text: t('navigation.registrations'), icon: <PersonAddIcon />, path: '/registrations', roles: ['ADMIN', 'REGISTRATOR'] },
    { text: t('navigation.accounting'), icon: <AccountBalanceIcon />, path: '/accounting', roles: ['ADMIN', 'ACCOUNTANT'] },
    { text: t('navigation.payment'), icon: <AccountBalanceIcon />, path: '/combined-payment', roles: ['ADMIN', 'REGISTRATOR'] },
    { text: t('navigation.diplomas'), icon: <SchoolIcon />, path: '/diplomas', roles: ['ADMIN', 'REGISTRATOR'] },
    { text: t('navigation.statistics'), icon: <BarChartIcon />, path: '/statistics', roles: ['ADMIN', 'STATISTICIAN', 'ACCOUNTANT'] },
    { text: t('navigation.admin'), icon: <SettingsIcon />, path: '/admin', roles: ['ADMIN'] },
  ].filter((item) => !item.roles || !user || item.roles.includes(user.role));

  // Активный раздел: по точному совпадению или по началу пути (чтобы /registrations/new подсвечивал «Регистрации»)
  const isItemActive = (path: string) => {
    if (path === '/') return location.pathname === '/';
    return location.pathname === path || location.pathname.startsWith(path + '/');
  };
  const activeItem = menuItems.find((i) => isItemActive(i.path));

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const drawer = (
    <Box>
      <Toolbar>
        <Typography variant="h6" noWrap component="div">
          FTR
        </Typography>
      </Toolbar>
      <Divider />
      <List dense disablePadding sx={{ pt: 0.5 }}>
        {menuItems.map((item) => (
          <ListItem key={item.text} disablePadding>
            <ListItemButton
              selected={isItemActive(item.path)}
              onClick={() => {
                navigate(item.path);
                if (isMobile) setMobileOpen(false);
              }}
              sx={{ py: 0.75, px: 2 }}
            >
              <ListItemIcon sx={{ minWidth: 36 }}>{item.icon}</ListItemIcon>
              <ListItemText
                primary={item.text}
                primaryTypographyProps={{ fontSize: '0.875rem', fontWeight: isItemActive(item.path) ? 600 : 400 }}
              />
            </ListItemButton>
          </ListItem>
        ))}
      </List>
      <Divider />
      <List>
        <ListItem disablePadding>
          <ListItemButton onClick={toggleDarkMode}>
            <ListItemIcon>{darkMode ? <Brightness7Icon /> : <Brightness4Icon />}</ListItemIcon>
            <ListItemText primary={darkMode ? t('common.lightTheme') : t('common.darkTheme')} />
          </ListItemButton>
        </ListItem>
        <ListItem disablePadding>
          <ListItemButton onClick={toggleLanguage}>
            <ListItemIcon>
              <LanguageIcon />
            </ListItemIcon>
            <ListItemText primary={i18n.language === 'ru' ? 'English' : 'Русский'} />
          </ListItemButton>
        </ListItem>
        <ListItem disablePadding>
          <ListItemButton onClick={handleLogout}>
            <ListItemIcon>
              <LogoutIcon />
            </ListItemIcon>
            <ListItemText primary={t('auth.logout')} />
          </ListItemButton>
        </ListItem>
      </List>
    </Box>
  );

  const bottomNavItems: Array<{ label: string; icon: React.ReactNode; path: string; roles?: string[] }> = [
    { label: 'Главная', icon: <DashboardIcon />, path: '/' },
    { label: 'Регистрации', icon: <PersonAddIcon />, path: '/registrations', roles: ['ADMIN', 'REGISTRATOR'] },
    { label: 'Оплата', icon: <AccountBalanceIcon />, path: '/combined-payment', roles: ['ADMIN', 'REGISTRATOR'] },
    { label: 'Дипломы', icon: <SchoolIcon />, path: '/diplomas', roles: ['ADMIN', 'REGISTRATOR'] },
    { label: 'Статистика', icon: <BarChartIcon />, path: '/statistics', roles: ['ADMIN', 'STATISTICIAN', 'ACCOUNTANT'] },
  ].filter((item) => !user || !item.roles || item.roles.includes(user.role));

  const bottomNavValue = bottomNavItems.findIndex((item) => isItemActive(item.path));

  return (
    <Box sx={{ display: 'flex' }}>
      <AppBar
        position="fixed"
        sx={{
          width: { md: `calc(100% - ${drawerWidth}px)` },
          ml: { md: `${drawerWidth}px` },
          backdropFilter: 'blur(8px)',
          bgcolor: 'rgba(13,18,32,0.85)',
          borderBottom: '1px solid',
          borderColor: 'divider',
        }}
      >
        <Toolbar sx={{ minHeight: { xs: 48, sm: 56 } }}>
          <IconButton 
            color="inherit" 
            edge="start" 
            onClick={handleDrawerToggle} 
            sx={{ 
              mr: 1, 
              display: { md: 'none' },
              fontSize: { xs: '1.25rem', sm: '1.5rem' }
            }}
          >
            <MenuIcon />
          </IconButton>
          <Typography 
            variant="subtitle1" 
            noWrap 
            component="div" 
            sx={{ 
              flexGrow: 1,
              fontWeight: 600,
              fontSize: { xs: '0.875rem', sm: '1rem' },
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              color: 'text.primary',
            }}
          >
            {activeItem?.text ?? t('navigation.home')}
          </Typography>

          <Tooltip title={user?.name ?? ''}>
            <Avatar
              sx={{ width: 32, height: 32, bgcolor: 'primary.main', fontSize: '0.875rem', fontWeight: 700, mr: 1 }}
            >
              {(user?.name ?? '?').charAt(0).toUpperCase()}
            </Avatar>
          </Tooltip>
          <Tooltip title={t('auth.logout')}>
            <IconButton color="inherit" onClick={handleLogout} sx={{ p: 1.5 }}>
              <LogoutIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Toolbar>
      </AppBar>
      <Box
        component="nav"
        sx={{ width: { md: drawerWidth }, flexShrink: { md: 0 } }}
      >
        <Drawer
          variant="temporary"
          open={mobileOpen}
          onClose={handleDrawerToggle}
          ModalProps={{ 
            keepMounted: true,
            // Оптимизация: отключить backdrop transition на медленных устройствах
            disableAutoFocus: true,
          }}
          transitionDuration={isMobile ? 200 : 225}
          sx={{
            display: { xs: 'block', md: 'none' },
            '& .MuiDrawer-paper': { 
              boxSizing: 'border-box', 
              width: drawerWidth,
              // Оптимизация производительности
              willChange: 'transform',
            },
          }}
        >
          {drawer}
        </Drawer>
        <Drawer
          variant="permanent"
          sx={{
            display: { xs: 'none', md: 'block' },
            '& .MuiDrawer-paper': { boxSizing: 'border-box', width: drawerWidth },
          }}
          open
        >
          {drawer}
        </Drawer>
      </Box>
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          p: { xs: 1, sm: 2, md: 3 },
          width: { md: `calc(100% - ${drawerWidth}px)` },
          mt: { xs: 6, sm: 7 },
          mb: { xs: 8, sm: 0 },
          minHeight: { xs: 'calc(100vh - 48px)', sm: 'calc(100vh - 56px)' },
        }}
      >
<Outlet />
        </Box>

      {/* Bottom navigation for mobile */}
      <Paper
        elevation={4}
        sx={{
          position: 'fixed',
          bottom: 0,
          left: 0,
          right: 0,
          zIndex: (theme) => theme.zIndex.appBar + 1,
          display: { md: 'none' },
          borderTop: '1px solid',
          borderColor: 'divider',
          backdropFilter: 'blur(8px)',
          bgcolor: 'rgba(13,18,32,0.95)',
        }}
      >
        <BottomNavigation
          value={bottomNavValue >= 0 ? bottomNavValue : 0}
          onChange={(_, newValue) => {
            navigate(bottomNavItems[newValue].path);
          }}
          sx={{
            bgcolor: 'transparent',
            '& .MuiBottomNavigationAction-root': {
              minWidth: { xs: 48, sm: 56 },
              '&.Mui-selected': { color: 'primary.main' },
              color: 'text.secondary',
            },
          }}
        >
          {bottomNavItems.map((item) => (
            <BottomNavigationAction
              key={item.path}
              label={item.label}
              icon={item.icon}
            />
          ))}
        </BottomNavigation>
      </Paper>
    </Box>
  );
};

